// @ts-strict-ignore
import React, { useState, useRef } from 'react';

import { AnimatedLoading } from '../../icons/AnimatedLoading';
import { useResponsive } from '../../ResponsiveProvider';
import { theme } from '../../style';
import { Error } from '../alerts';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { Paragraph } from '../common/Paragraph';
import { View } from '../common/View';
import { type CommonModalProps } from '../Modals';

function renderError(error) {
  let message;
  switch (error) {
    case 'timeout':
      message = 'Timed out. Please try again.';
      break;
    case 'abort':
      message =
        'You have cancelled the connection process to your financial institution. If that was not intentional, please try again.';
      break;
    case 'error':
      message =
        'An issue occurred when connecting to your financial institution, please try again.';
      break;
    default:
      message = 'An error occurred while linking your account, sorry!';
      break;
  }
  return <Error style={{ alignSelf: 'center' }}>{message}</Error>;
}

type ProviderCardProps = {
  name: string;
  logo: string;
  logoHeight: number;
  helperText: string;
  onConnect: () => void;
  disabled?: boolean;
};

function ProviderCard({
  name,
  logo,
  logoHeight,
  helperText,
  onConnect,
  disabled,
}: ProviderCardProps) {
  return (
    <View
      style={{
        border: `1px solid ${theme.tableBorder}`,
        borderRadius: 8,
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        backgroundColor: theme.tableBackground,
        flex: 1,
        minWidth: 0,
        minHeight: 140,
        flexShrink: 0,
      }}
    >
      <View
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
          flex: 1,
        }}
      >
        <img
          src={logo}
          alt={`${name} logo`}
          style={{
            height: logoHeight,
            width: 'auto',
            objectFit: 'contain',
            flexShrink: 0,
          }}
        />
        <View
          style={{
            fontSize: 13,
            color: theme.pageTextLight,
            textAlign: 'center',
            minHeight: 32,
          }}
        >
          {helperText}
        </View>
      </View>
      <Button
        type="normal"
        style={{
          padding: '10px 0',
          fontSize: 14,
          flexShrink: 0,
        }}
        onClick={onConnect}
        disabled={disabled}
      >
        Connect with {name}
      </Button>
    </View>
  );
}

type HelpLinkProps = {
  question: string;
  answer: string;
};

function HelpLink({ question, answer }: HelpLinkProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <View style={{ marginTop: 8 }}>
      <View
        style={{
          color: theme.gsGreen700,
          fontSize: 13,
          cursor: 'pointer',
          textDecoration: 'underline',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        {question}
      </View>
      {expanded && (
        <View
          style={{
            marginTop: 8,
            padding: 10,
            backgroundColor: theme.tableRowBackgroundHover,
            borderRadius: 4,
            fontSize: 13,
            color: theme.pageText,
            display: 'inline-block',
          }}
        >
          <div dangerouslySetInnerHTML={{ __html: answer }}/>
        </View>
      )}
    </View>
  );
}

type QuilttExternalMsgProps = {
  modalProps: CommonModalProps;
  isRepairFlow: boolean;
  onMoveExternal: (connectorName: string) => Promise<{ error; data }>;
  onSuccess: (data: unknown) => Promise<void>;
  onClose?: () => void;
};

export function QuilttExternalMsg({
  modalProps,
  isRepairFlow,
  onMoveExternal,
  onSuccess,
  onClose: originalOnClose,
}: QuilttExternalMsgProps) {
  const [waiting, setWaiting] = useState(null);
  const [completionReason, setCompletionReason] = useState('');
  const [error, setError] = useState(null);
  const { isWideWidth } = useResponsive();
  const data = useRef(null);

  async function onJump(connectorName: string) {
    setError(null);
    setWaiting('browser');

    const res = await onMoveExternal(connectorName);
    if (res.error) {
      setError(res.error);
      setWaiting(null);
      return;
    }

    data.current = res.data;
    setWaiting(null);
    setCompletionReason(data.current.completion_reason);

    if (
      data.current.is_completed &&
      data.current.completion_reason !== 'success'
    ) {
      setError(data.current.completion_reason);
    }
  }

  function onClose() {
    originalOnClose?.();
    modalProps.onClose();
  }

  async function onContinue() {
    setWaiting('accounts');
    await onSuccess(data.current);
    setWaiting(null);
  }

  async function onSmartMatch() {
    // For now, default to 'main' connector for Smart Match
    // This can be enhanced later to actually search and match
    await onJump('main');
  }

  const providers = [
    {
      name: 'MX',
      logo: '/images/provider-logos/mx.png',
      logoHeight: 32,
      helperText:
        'Comprehensive bank coverage with data enrichment. Default choice for US users',
      connector: 'mx',
    },
    {
      name: 'Finicity',
      logo: '/images/provider-logos/finicity.webp',
      logoHeight: 50,
      helperText:
        'Secure open-banking connections with extensive bank support, by Mastercard',
      connector: 'finicity',
    },
    {
      name: 'Plaid',
      logo: '/images/provider-logos/plaid.png',
      logoHeight: 32,
      helperText:
        'Fast setup with smooth connection experience. Great alternative for outside of US',
      connector: 'plaid',
    },
  ];

  function renderContent() {
    if (waiting) {
      return (
        <View style={{ alignItems: 'center', marginTop: 15 }}>
          <AnimatedLoading
            color={theme.pageTextDark}
            style={{ width: 20, height: 20 }}
          />
          <View style={{ marginTop: 10, color: theme.pageText }}>
            {waiting === 'browser'
              ? 'Connecting to your financial institution (in new tab)...'
              : waiting === 'accounts'
                ? 'Loading accounts...'
                : null}
          </View>
        </View>
      );
    }

    if (completionReason === 'success') {
      return (
        <Button
          type="primary"
          style={{
            padding: '10px 0',
            fontSize: 15,
            fontWeight: 600,
            marginTop: 10,
          }}
          onClick={onContinue}
        >
          Success! Click to continue &rarr;
        </Button>
      );
    }

    // Repair flow: just show a message and single CTA
    if (isRepairFlow) {
      return (
        <>
          <Paragraph style={{ fontSize: 15, marginBottom: 20 }}>
            Your connection needs to be re-authorized. Click the button below to
            reconnect using your existing provider.
          </Paragraph>

          {error && (
            <View style={{ marginBottom: 20, flexShrink: 0 }}>
              {renderError(error)}
            </View>
          )}

          <Button
            type="primary"
            style={{
              padding: '12px 0',
              fontSize: 15,
              fontWeight: 600,
              width: '100%',
            }}
            onClick={() => onJump('')}
          >
            Reconnect to Financial Institution
          </Button>
        </>
      );
    }

    // Normal flow: show all provider options
    return (
      <View style={{ padding: 10 }}>
        <Paragraph style={{ fontSize: 15, marginBottom: 20 }}>
          Choose a secure connector below. We support MX, Finicity, and Plaid.
          If you&apos;re not sure, use Smart Match.
        </Paragraph>

        {error && (
          <View style={{ marginBottom: 20, flexShrink: 0 }}>
            {renderError(error)}
          </View>
        )}

        {/* Smart Match Section */}
        <View
          style={{
            border: `2px solid ${theme.buttonPrimaryBorder}`,
            borderRadius: 8,
            padding: 20,
            marginBottom: 20,
            backgroundColor: theme.tableBackground,
            minHeight: 140,
            flexShrink: 0,
          }}
        >
          <View
            style={{
              fontSize: 16,
              fontWeight: 600,
              marginBottom: 8,
              color: theme.pageTextDark,
            }}
          >
            Smart Match
          </View>
          <View
            style={{
              fontSize: 14,
              marginBottom: 12,
              color: theme.pageTextLight,
            }}
          >
            Search your bank and we'll try to find the best match (eg:
            prioritization of OAuth connections)
          </View>
          <Button
            type="primary"
            style={{
              padding: '10px 0',
              fontSize: 15,
              fontWeight: 600,
              width: '100%',
            }}
            onClick={onSmartMatch}
          >
            Continue &rarr;
          </Button>
        </View>

        {/* Divider */}
        <View
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 20,
            position: 'relative',
            height: 20,
          }}
        >
          <View
            style={{
              position: 'absolute',
              width: '100%',
              height: 1,
              backgroundColor: theme.tableBorder,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          />
          <View
            style={{
              position: 'relative',
              backgroundColor: theme.modalBackground,
              padding: '0 15px',
              paddingBottom: 15,
              fontSize: 13,
              color: theme.pageTextLight,
              zIndex: 1,
            }}
          >
            Or connect directly with a provider of your choice
          </View>
        </View>

        {/* Provider Cards */}
        <View
          style={{
            display: 'flex',
            flexDirection: isWideWidth ? 'row' : 'column',
            gap: 12,
            flexShrink: 0,
          }}
        >
          {providers.map(provider => (
            <ProviderCard
              key={provider.name}
              name={provider.name}
              logo={provider.logo}
              logoHeight={provider.logoHeight}
              helperText={provider.helperText}
              onConnect={() => onJump(provider.connector)}
              disabled={!!waiting}
            />
          ))}
        </View>

        {/* Footer Help Links */}
        <View
          style={{
            marginTop: 24,
            paddingTop: 20,
            borderTop: `1px solid ${theme.tableBorder}`,
            flexShrink: 0,
          }}
        >
          <HelpLink
            question="Why three providers?"
            answer="We integrate with the top 3 trusted data aggregators to give our users the most coverage and choice. Your credentials are encrypted and never stored by Good Steward."
          />
          <HelpLink
            question="Having trouble?"
            answer="The experience of connecting to your bank can be frustrating at times. <a href='mailto:support@goodsteward.io'>Contact us</a> and we'll help get your sorted."
          />
          <HelpLink
            question="Couldn't find your bank?"
            answer="Try another name or use <a target='_blank' href='https://chatwoot.goodsteward.io/hc/gs-kb/articles/1748442663-import'>manual import</a>"
          />
        </View>
      </View>
    );
  }

  return (
    <Modal
      title="Connect Your Financial Institution"
      {...modalProps}
      onClose={onClose}
      style={{
        maxWidth: isRepairFlow ? 400 : 750,
        width: '100%',
      }}
    >
      {() => (
        <View
          style={{
            overflowY: 'auto',
            flex: 1,
          }}
        >
          {renderContent()}
        </View>
      )}
    </Modal>
  );
}
